import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../components/ThemeContext';
import { useResponsive } from '../components/useResponsive';
import { API_BASE } from '../../config';

// ============================================================
// TYPES
// ============================================================

interface Finding {
  type: string;
  category: string;
  suggestion?: string;
  impact?: string;
  source?: string;
  factor?: string;
  result?: string;
  potential_weight_gain?: number;
  current_weight_pct?: number;
  suggested_weight_pct?: number;
  weight_delta?: number;
  suggested_direction?: string;
  potential_factors?: string[];
  existing_factors?: string[];
  priority?: string;
  sources_affected?: string[];
  details?: string;
  ai_generated?: boolean;
  [key: string]: unknown;
}

interface ProductFinding {
  id: number;
  ndc: string;
  product_name: string;
  manufacturer: string;
  coo_country: string;
  coo_score: number;
  quality_tier: string;
  findings: Finding[];
  summary: string;
  new_sources_count: number;
  new_factors_count: number;
  data_discrepancies_count: number;
  missed_factors_count: number;
  rescrape_flags_count: number;
  logic_adjustments_count: number;
  weight_adjustments_count: number;
  potential_score_improvement: number;
  analyzed_at: string;
}

interface SummaryData {
  agent_id: string;
  total_products_analyzed: number;
  avg_score: number;
  avg_potential_improvement: number;
  total_new_sources_suggested: number;
  total_new_factors_suggested: number;
  total_data_discrepancies: number;
  total_missed_factors: number;
  total_rescrape_flags: number;
  total_logic_adjustments: number;
  total_weight_adjustments: number;
  first_analyzed: string | null;
  last_analyzed: string | null;
}

interface AiNarrativeData {
  ai_narrative: string;
  ai_generated: boolean;
  total_products_analyzed: number;
}

// ============================================================
// AGENT DISPLAY NAMES
// ============================================================

const AGENT_DISPLAY: Record<string, string> = {
  coo_scoring: 'COO Scoring Agent',
  ndc_lookup: 'NDC Lookup Agent',
  qrs: 'Quality Rating System',
  pis: 'Pricing Intelligence System',
  coors: 'COORS Agent',
  pns: 'Political Nexus Scoring',
};

// ============================================================
// HELPERS
// ============================================================

function formatTimestamp(iso: string | null): string {
  if (!iso) return '--';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '--';
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

function tierColor(tier: string): string {
  switch (tier) {
    case 'HIGH': return '#22C55E';
    case 'MODERATE': return '#F59E0B';
    case 'LOW': return '#EF4444';
    default: return '#94A3B8';
  }
}

function priorityColor(priority: string): string {
  switch (priority) {
    case 'high': return '#EF4444';
    case 'medium': return '#F59E0B';
    case 'low': return '#22C55E';
    default: return '#94A3B8';
  }
}

function categoryIcon(t: string): string {
  switch (t) {
    case 'new_source': return 'M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z';
    case 'new_factor': return 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2';
    case 'data_discrepancy': case 'missed_factor': return 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z';
    case 'rescrape_flag': return 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15';
    case 'logic_adjustment': return 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z';
    case 'weight_adjustment': return 'M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3';
    default: return 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z';
  }
}

function categoryLabel(t: string): string {
  switch (t) {
    case 'new_source': return 'New Source';
    case 'new_factor': return 'New Factor';
    case 'data_discrepancy': return 'Discrepancy';
    case 'missed_factor': return 'Missed Factor';
    case 'rescrape_flag': return 'Rescrape';
    case 'logic_adjustment': return 'Logic Adj.';
    case 'weight_adjustment': return 'Weight Adj.';
    default: return t;
  }
}

function categoryColor(t: string): string {
  switch (t) {
    case 'new_source': return '#8B5CF6';
    case 'new_factor': return '#3B82F6';
    case 'data_discrepancy': case 'missed_factor': return '#EF4444';
    case 'rescrape_flag': return '#F59E0B';
    case 'logic_adjustment': return '#06B6D4';
    case 'weight_adjustment': return '#10B981';
    default: return '#94A3B8';
  }
}

// ============================================================
// COMPONENT
// ============================================================

export default function SuggestedScoringImprovements() {
  const { agentName } = useParams<{ agentName: string }>();
  const navigate = useNavigate();
  const { T } = useTheme();
  const { isMobile } = useResponsive();

  const [findings, setFindings] = useState<ProductFinding[]>([]);
  const [summary, setSummary] = useState<SummaryData | null>(null);
  const [aiNarrative, setAiNarrative] = useState<AiNarrativeData | null>(null);
  const [narrativeLoading, setNarrativeLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [filterCategory, setFilterCategory] = useState<string>('all');

  const agentDisplay = AGENT_DISPLAY[agentName || ''] || (agentName || '').replace(/_/g, ' ');
  const isQrs = agentName === 'qrs';

  const fetchNarrative = useCallback(async () => {
    setNarrativeLoading(true);
    try {
      const resp = await fetch(
        `${API_BASE}/api/metasupply/swarm/data-inspector/ai-narrative?agent_id=${agentName || 'coo_scoring'}`,
        { credentials: 'include' },
      );
      if (resp.ok) {
        const data = await resp.json();
        setAiNarrative(data);
      }
    } catch {
      // silently ignore — narrative is optional
    } finally {
      setNarrativeLoading(false);
    }
  }, [agentName]);

  const fetchData = useCallback(async () => {
    try {
      const [findingsResp, summaryResp] = await Promise.all([
        fetch(`${API_BASE}/api/metasupply/swarm/data-inspector/findings?agent_id=${agentName || 'coo_scoring'}&limit=200`, { credentials: 'include' }),
        fetch(`${API_BASE}/api/metasupply/swarm/data-inspector/summary?agent_id=${agentName || 'coo_scoring'}`, { credentials: 'include' }),
      ]);

      if (findingsResp.ok) {
        const data = await findingsResp.json();
        setFindings(data.findings || []);
      } else {
        setError('Failed to fetch analysis findings');
      }

      if (summaryResp.ok) {
        const data = await summaryResp.json();
        setSummary(data);
      }

      setError(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [agentName]);

  useEffect(() => {
    fetchData();
    fetchNarrative();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData, fetchNarrative]);

  const toggleRow = (key: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const filteredFindings = findings.filter(pf => {
    if (filterCategory === 'all') return true;
    return pf.findings.some(f => f.type === filterCategory);
  });

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 40, height: 40, border: `3px solid ${T.border}`,
            borderTopColor: T.gold, borderRadius: '50%',
            animation: 'spin 1s linear infinite', margin: '0 auto 16px',
          }} />
          <span style={{ fontSize: 14, color: T.textLight }}>Loading analysis findings...</span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* Back link + Header */}
      <div>
        <button
          onClick={() => navigate(`/agent-detail/${agentName}`)}
          style={{
            border: 'none', background: 'none', cursor: 'pointer', fontSize: 13,
            color: T.navyLight, fontWeight: 600, padding: 0, fontFamily: 'inherit',
            display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Back to {agentDisplay}
        </button>

        <div style={{
          background: T.offWhite, border: `1px solid ${T.border}`,
          borderRadius: 16, padding: isMobile ? '24px 16px' : '32px 40px',
          display: 'flex', flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: 20,
        }}>
          <div>
            <h1 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 800, color: T.textDark, margin: 0, marginBottom: 8, fontFamily: "'Playfair Display', Georgia, serif" }}>
              Suggested Scoring Improvements
            </h1>
            <p style={{ fontSize: 14, color: T.textMid, maxWidth: 600, lineHeight: 1.6, margin: 0 }}>
              Data Inspector analysis of rescored products. Identifies new evidence sources, data discrepancies, and scoring logic improvements.
            </p>
          </div>
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
            background: `${T.navy}08`, borderRadius: 12, padding: '16px 24px',
          }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: T.red, textTransform: 'uppercase', letterSpacing: '0.12em', fontFamily: "'IBM Plex Mono', 'Courier New', monospace" }}>
              Analyzed
            </span>
            <span style={{ fontSize: 32, fontWeight: 800, color: T.textDark }}>
              {summary?.total_products_analyzed ?? findings.length}
            </span>
          </div>
        </div>
      </div>

      {error && (
        <div style={{
          padding: '12px 16px', borderRadius: T.radiusSm,
          background: T.dangerBg, border: `1px solid ${T.danger}30`,
          color: T.danger, fontSize: 13, fontWeight: 600,
        }}>
          {error}
        </div>
      )}

      {/* AI Narrative Summary */}
      {(aiNarrative?.ai_narrative || narrativeLoading) && (
        <div style={{
          background: `linear-gradient(135deg, ${T.white} 0%, #F8F6FF 100%)`,
          border: '1px solid #8B5CF630',
          borderRadius: T.radius,
          padding: isMobile ? '20px 16px' : '24px 28px',
          boxShadow: T.shadow,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2">
              <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#7C3AED' }}>
              AI Executive Summary
            </span>
            <span style={{
              fontSize: 9, fontWeight: 700, textTransform: 'uppercase' as const,
              padding: '2px 8px', borderRadius: 8,
              background: 'linear-gradient(135deg, #8B5CF620, #3B82F620)',
              color: '#7C3AED',
              border: '1px solid #8B5CF630',
              letterSpacing: '0.04em',
            }}>
              AI Generated
            </span>
            <button
              onClick={() => fetchNarrative()}
              disabled={narrativeLoading}
              style={{
                marginLeft: 'auto', padding: '4px 12px', borderRadius: 8,
                border: `1px solid #8B5CF630`, background: narrativeLoading ? '#8B5CF610' : 'transparent',
                color: '#7C3AED', fontSize: 11, fontWeight: 600,
                cursor: narrativeLoading ? 'default' : 'pointer', fontFamily: 'inherit',
              }}
            >
              {narrativeLoading ? 'Generating...' : 'Regenerate'}
            </button>
          </div>
          {narrativeLoading && !aiNarrative?.ai_narrative ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 16, height: 16, border: '2px solid #8B5CF630',
                borderTopColor: '#7C3AED', borderRadius: '50%',
                animation: 'spin 1s linear infinite',
              }} />
              <span style={{ fontSize: 13, color: T.textLight }}>Claude is analyzing all findings...</span>
            </div>
          ) : (
            <p style={{
              fontSize: 14, color: T.textDark, lineHeight: 1.7, margin: 0,
              fontStyle: 'normal',
            }}>
              {aiNarrative?.ai_narrative}
            </p>
          )}
        </div>
      )}

      {/* Summary Cards */}
      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: 12 }}>
          {[
            { label: 'New Sources', value: summary.total_new_sources_suggested, color: '#8B5CF6' },
            { label: 'New Factors', value: summary.total_new_factors_suggested, color: '#3B82F6' },
            { label: 'Discrepancies', value: summary.total_data_discrepancies, color: '#EF4444' },
            { label: 'Missed Factors', value: summary.total_missed_factors, color: '#F97316' },
            { label: 'Rescrape Flags', value: summary.total_rescrape_flags, color: '#F59E0B' },
            { label: 'Logic Adjustments', value: summary.total_logic_adjustments, color: '#06B6D4' },
            { label: 'Weight Adjustments', value: summary.total_weight_adjustments, color: '#10B981' },
          ].map(stat => (
            <div key={stat.label} style={{
              background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius,
              padding: 16, boxShadow: T.shadow, textAlign: 'center',
            }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
                {stat.label}
              </div>
              <div style={{ fontSize: 28, fontWeight: 800, color: stat.color }}>
                {stat.value}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Aggregate improvement stats */}
      {summary && (
        <div style={{
          background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius,
          padding: 20, boxShadow: T.shadow,
          display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 24,
          alignItems: isMobile ? 'flex-start' : 'center',
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>
              Average Current Score
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: T.textDark }}>
              {summary.avg_score.toFixed(1)}%
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>
              Avg Potential Improvement
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#22C55E' }}>
              +{summary.avg_potential_improvement.toFixed(1)}%
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>
              Last Analyzed
            </div>
            <div style={{ fontSize: 14, fontWeight: 600, color: T.textDark }}>
              {formatTimestamp(summary.last_analyzed)}
            </div>
          </div>
          <button
            onClick={() => fetchData()}
            style={{
              padding: '8px 20px', borderRadius: 8, border: `1px solid ${T.gold}40`,
              background: `${T.gold}12`, color: T.gold, fontSize: 12, fontWeight: 700,
              cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
            }}
          >
            Refresh
          </button>
        </div>
      )}

      {/* Category Filter */}
      {findings.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {[
            { key: 'all', label: 'All' },
            { key: 'new_source', label: 'New Sources' },
            { key: 'new_factor', label: 'New Factors' },
            { key: 'data_discrepancy', label: 'Discrepancies' },
            { key: 'missed_factor', label: 'Missed Factors' },
            { key: 'rescrape_flag', label: 'Rescrape Flags' },
            { key: 'logic_adjustment', label: 'Logic Adjustments' },
            { key: 'weight_adjustment', label: 'Weight Adjustments' },
          ].map(cat => (
            <button
              key={cat.key}
              onClick={() => setFilterCategory(cat.key)}
              style={{
                padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                border: `1px solid ${filterCategory === cat.key ? T.navy : T.border}`,
                background: filterCategory === cat.key ? `${T.navy}12` : T.white,
                color: filterCategory === cat.key ? T.navy : T.textMid,
                cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
              }}
            >
              {cat.label}
            </button>
          ))}
        </div>
      )}

      {/* Product Findings Table */}
      <div style={{
        background: T.white, border: `1px solid ${T.border}`,
        borderRadius: T.radius, boxShadow: T.shadow, overflow: 'hidden',
      }}>
        {filteredFindings.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>--</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: T.textDark, marginBottom: 8 }}>
              No Analysis Findings Yet
            </div>
            <div style={{ fontSize: 13, color: T.textLight, maxWidth: 400, margin: '0 auto' }}>
              The Data Inspector runs automatically when products are rescored.
              Use the Re-score feature on the agent detail page to trigger analysis.
            </div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${T.border}` }}>
                  {(['NDC #', 'Product Name', 'Manufacturer', ...(isQrs ? [] : ['COO']), 'Score', 'Findings', 'Potential +', 'Analyzed'] as string[]).map(col => (
                    <th key={col} style={{
                      padding: '12px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700,
                      color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.08em',
                      whiteSpace: 'nowrap',
                    }}>
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredFindings.map((pf, idx) => {
                  const rowKey = `${pf.ndc}-${idx}`;
                  const isExpanded = expandedRows.has(rowKey);
                  const displayFindings = filterCategory === 'all'
                    ? pf.findings
                    : pf.findings.filter(f => f.type === filterCategory);
                  const totalFindingsCount = pf.new_sources_count + pf.new_factors_count
                    + pf.data_discrepancies_count + (pf.missed_factors_count || 0)
                    + pf.rescrape_flags_count
                    + pf.logic_adjustments_count + (pf.weight_adjustments_count || 0);

                  return (
                    <React.Fragment key={rowKey}>
                      <tr
                        onClick={() => toggleRow(rowKey)}
                        style={{
                          borderBottom: `1px solid ${T.borderLight}`,
                          cursor: 'pointer',
                          background: isExpanded ? `${T.gold}08` : 'transparent',
                          transition: 'background 0.15s',
                        }}
                        onMouseEnter={e => { if (!isExpanded) (e.currentTarget as HTMLTableRowElement).style.background = `${T.navy}04`; }}
                        onMouseLeave={e => { if (!isExpanded) (e.currentTarget as HTMLTableRowElement).style.background = 'transparent'; }}
                      >
                        <td style={{ padding: '12px 16px', fontWeight: 600, color: T.navy, whiteSpace: 'nowrap' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <svg
                              width="14" height="14" viewBox="0 0 24 24" fill="none"
                              stroke={T.textLight} strokeWidth="2"
                              style={{
                                transition: 'transform 0.2s',
                                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                flexShrink: 0,
                              }}
                            >
                              <path d="M6 9l6 6 6-6" />
                            </svg>
                            {pf.ndc}
                          </div>
                        </td>
                        <td style={{ padding: '12px 16px', color: T.textDark, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {pf.product_name || '--'}
                        </td>
                        <td style={{ padding: '12px 16px', color: T.textDark }}>
                          {pf.manufacturer || '--'}
                        </td>
                        {!isQrs && (
                          <td style={{ padding: '12px 16px', color: T.textDark }}>
                            {pf.coo_country || '--'}
                          </td>
                        )}
                        <td style={{ padding: '12px 16px' }}>
                          <span style={{
                            fontSize: 9, fontWeight: 700, textTransform: 'uppercase',
                            letterSpacing: '0.04em', padding: '2px 8px', borderRadius: 10,
                            background: `${tierColor(pf.quality_tier)}18`,
                            color: tierColor(pf.quality_tier),
                          }}>
                            {pf.quality_tier || '--'}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <span style={{
                            fontSize: 12, fontWeight: 700, color: T.textDark,
                            background: `${T.navy}08`, padding: '2px 10px', borderRadius: 10,
                          }}>
                            {totalFindingsCount}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <span style={{
                            fontSize: 13, fontWeight: 700,
                            color: pf.potential_score_improvement > 0 ? '#22C55E' : T.textLight,
                          }}>
                            {pf.potential_score_improvement > 0 ? `+${pf.potential_score_improvement.toFixed(1)}%` : '--'}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px', color: T.textLight, fontSize: 11, whiteSpace: 'nowrap' }}>
                          {formatTimestamp(pf.analyzed_at)}
                        </td>
                      </tr>

                      {/* Expanded findings */}
                      {isExpanded && (
                        <tr>
                          <td colSpan={isQrs ? 7 : 8} style={{ padding: 0, background: T.offWhite }}>
                            <div style={{ padding: '16px 24px' }}>
                              {/* Summary */}
                              {pf.summary && (
                                <div style={{
                                  padding: '12px 16px', borderRadius: T.radiusSm,
                                  background: `${T.navy}06`, border: `1px solid ${T.navy}15`,
                                  marginBottom: 16, fontSize: 13, color: T.textDark, lineHeight: 1.6,
                                }}>
                                  <span style={{ fontWeight: 700, color: T.navy }}>Summary: </span>
                                  {pf.summary}
                                </div>
                              )}

                              {/* Category counts */}
                              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                                {[
                                                  { label: 'New Sources', count: pf.new_sources_count, cat: 'new_source' },
                                                  { label: 'New Factors', count: pf.new_factors_count, cat: 'new_factor' },
                                                  { label: 'Discrepancies', count: pf.data_discrepancies_count, cat: 'data_discrepancy' },
                                                  { label: 'Missed Factors', count: pf.missed_factors_count || 0, cat: 'missed_factor' },
                                                  { label: 'Rescrape', count: pf.rescrape_flags_count, cat: 'rescrape_flag' },
                                                  { label: 'Logic Adj.', count: pf.logic_adjustments_count, cat: 'logic_adjustment' },
                                                  { label: 'Weight Adj.', count: pf.weight_adjustments_count || 0, cat: 'weight_adjustment' },
                                ].filter(c => c.count > 0).map(c => (
                                  <span key={c.cat} style={{
                                    fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 12,
                                    background: `${categoryColor(c.cat)}15`,
                                    color: categoryColor(c.cat),
                                    border: `1px solid ${categoryColor(c.cat)}30`,
                                  }}>
                                    {c.label}: {c.count}
                                  </span>
                                ))}
                              </div>

                              {/* Individual findings */}
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {displayFindings.map((finding, fIdx) => {
                                  const fType = finding.type || '';
                                  const fColor = categoryColor(fType);
                                  const impactStr = typeof finding.impact === 'string' ? finding.impact : '';
                                  return (
                                  <div key={fIdx} style={{
                                    background: T.white, border: `1px solid ${T.borderLight}`,
                                    borderRadius: T.radiusSm, padding: '12px 16px',
                                    borderLeft: `3px solid ${fColor}`,
                                  }}>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                        stroke={fColor} strokeWidth="2"
                                        style={{ flexShrink: 0, marginTop: 2 }}
                                      >
                                        <path d={categoryIcon(fType)} />
                                      </svg>
                                      <div style={{ flex: 1 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                                          <span style={{
                                            fontSize: 9, fontWeight: 700, textTransform: 'uppercase',
                                            padding: '1px 6px', borderRadius: 8,
                                            background: `${fColor}15`, color: fColor,
                                          }}>
                                            {categoryLabel(fType)}
                                          </span>
                                          {finding.category && finding.category !== fType && (
                                            <span style={{
                                              fontSize: 9, fontWeight: 600, textTransform: 'uppercase',
                                              padding: '1px 6px', borderRadius: 8,
                                              background: `${T.navy}08`, color: T.textMid,
                                            }}>
                                              {finding.category.replace(/_/g, ' ')}
                                            </span>
                                          )}
                                          {finding.ai_generated && (
                                            <span style={{
                                              fontSize: 9, fontWeight: 700, textTransform: 'uppercase',
                                              padding: '1px 6px', borderRadius: 8,
                                              background: 'linear-gradient(135deg, #8B5CF620, #3B82F620)',
                                              color: '#7C3AED',
                                              border: '1px solid #8B5CF630',
                                              letterSpacing: '0.04em',
                                            }}>
                                              AI Generated
                                            </span>
                                          )}
                                          {impactStr && (
                                            <span style={{
                                              fontSize: 9, fontWeight: 700, textTransform: 'uppercase',
                                              padding: '1px 6px', borderRadius: 8,
                                              background: `${priorityColor(impactStr)}15`,
                                              color: priorityColor(impactStr),
                                            }}>
                                              {impactStr}
                                            </span>
                                          )}
                                          {finding.priority && (
                                            <span style={{
                                              fontSize: 9, fontWeight: 700, textTransform: 'uppercase',
                                              padding: '1px 6px', borderRadius: 8,
                                              background: `${priorityColor(finding.priority)}15`,
                                              color: priorityColor(finding.priority),
                                            }}>
                                              {finding.priority}
                                            </span>
                                          )}
                                        </div>

                                        {/* Factor & Source header */}
                                        {finding.factor && (
                                          <div style={{ fontSize: 13, fontWeight: 700, color: T.textDark, marginBottom: 2 }}>
                                            {finding.factor}
                                            {finding.source && (
                                              <span style={{ fontWeight: 400, color: T.textLight, fontSize: 11 }}>
                                                {' '}({finding.source})
                                              </span>
                                            )}
                                          </div>
                                        )}

                                        {/* Main suggestion / description text */}
                                        {finding.suggestion && (
                                          <div style={{ fontSize: 12, color: T.textMid, lineHeight: 1.6, marginBottom: 6 }}>
                                            {String(finding.suggestion)}
                                          </div>
                                        )}

                                        {/* Evidence result for missed factors */}
                                        {finding.result && (
                                          <div style={{ fontSize: 11, color: T.textLight, lineHeight: 1.4, marginBottom: 4 }}>
                                            <span style={{ fontWeight: 600 }}>Evidence: </span>
                                            {String(finding.result).slice(0, 300)}
                                          </div>
                                        )}

                                        {/* Weight adjustment details */}
                                        {fType === 'weight_adjustment' && finding.current_weight_pct != null && (
                                          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 4 }}>
                                            <span style={{ fontSize: 11, color: T.textLight }}>
                                              <span style={{ fontWeight: 600 }}>Current: </span>{finding.current_weight_pct}%
                                            </span>
                                            <span style={{ fontSize: 11, color: T.textLight }}>
                                              <span style={{ fontWeight: 600 }}>Suggested: </span>{finding.suggested_weight_pct}%
                                            </span>
                                            <span style={{
                                              fontSize: 11, fontWeight: 600,
                                              color: (finding.weight_delta || 0) > 0 ? '#22C55E' : '#EF4444',
                                            }}>
                                              {(finding.weight_delta || 0) > 0 ? '+' : ''}{finding.weight_delta}%
                                            </span>
                                          </div>
                                        )}

                                        {/* Potential factors for new sources */}
                                        {finding.potential_factors && (finding.potential_factors as string[]).length > 0 && (
                                          <div style={{ fontSize: 11, color: T.textLight, marginBottom: 4 }}>
                                            <span style={{ fontWeight: 600 }}>Potential Factors: </span>
                                            {(finding.potential_factors as string[]).join(', ')}
                                          </div>
                                        )}

                                        {/* Existing factors context for new factors */}
                                        {finding.existing_factors && (finding.existing_factors as string[]).length > 0 && (
                                          <div style={{ fontSize: 11, color: T.textLight, marginBottom: 4 }}>
                                            <span style={{ fontWeight: 600 }}>Existing Factors: </span>
                                            {(finding.existing_factors as string[]).join(', ')}
                                          </div>
                                        )}

                                        {/* Sources affected for rescrape */}
                                        {finding.sources_affected && (finding.sources_affected as string[]).length > 0 && (
                                          <div style={{ fontSize: 11, color: T.textLight, marginBottom: 4 }}>
                                            <span style={{ fontWeight: 600 }}>Sources: </span>
                                            {(finding.sources_affected as string[]).join(', ')}
                                          </div>
                                        )}

                                        {/* Details text (used by some finding types) */}
                                        {finding.details && (
                                          <div style={{ fontSize: 11, color: T.textLight, lineHeight: 1.4, marginBottom: 4 }}>
                                            <span style={{ fontWeight: 600 }}>Details: </span>
                                            {String(finding.details)}
                                          </div>
                                        )}

                                        {/* Potential weight gain for missed factors */}
                                        {finding.potential_weight_gain != null && finding.potential_weight_gain > 0 && (
                                          <div style={{ fontSize: 11, fontWeight: 600, color: '#22C55E', marginTop: 4 }}>
                                            Potential score gain: +{finding.potential_weight_gain.toFixed(1)}%
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  );
                                })}
                              </div>

                              {displayFindings.length === 0 && (
                                <div style={{ padding: 16, textAlign: 'center', color: T.textLight, fontSize: 13 }}>
                                  No findings in this category for this product.
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
